import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSession } from '~/lib/auth/useSession';
import { appStore } from '~/stores/useAppStore';
import { Spinner } from './ui/spinner';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { AlertCircle, RefreshCw } from 'lucide-react';

interface AuthContextValue {
  isAuthenticated: boolean;
  isInitializing: boolean;
  user: { id: string; email: string; is_admin: boolean; avatar_url?: string } | null;
}

const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  isInitializing: true,
  user: null,
});

export function useAuth() {
  return useContext(AuthContext);
}

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider - Single source of truth for authentication state
 *
 * This component handles:
 * 1. Initial session check on app load
 * 2. Unified loading state (no flash of login page)
 * 3. Automatic redirects based on auth state and current route
 * 4. Syncing auth state to Zustand store
 * 5. Loading initial data (projects/buckets) on authentication
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: session, isLoading, isError, error, refetch } = useSession();
  const hasLoadedDataRef = useRef(false);
  const hasRedirectedRef = useRef(false);

  const isLoginPage = location.pathname === '/login';
  const isAuthenticated = session?.authenticated ?? false;

  // Determine if we're still initializing (first load, no data yet)
  // After first load, session will be defined (even if authenticated: false)
  const isInitializing = isLoading && session === undefined;

  // Sync session user to appStore
  useEffect(() => {
    if (session?.authenticated && session.user) {
      appStore.getState().setUser(session.user);
    } else if (session !== undefined) {
      appStore.getState().setUser(null);
    }
  }, [session?.authenticated, session?.user, session]);

  // Load projects and buckets when authenticated
  useEffect(() => {
    if (session?.authenticated && !hasLoadedDataRef.current) {
      hasLoadedDataRef.current = true;

      Promise.all([
        appStore.getState().refreshProjects(true),
        appStore.getState().refreshBuckets(undefined, true)
      ]).catch(err => {
        console.error('[AuthProvider] Failed to load initial data:', err);
        hasLoadedDataRef.current = false;
      });
    }

    if (!session?.authenticated) {
      hasLoadedDataRef.current = false;
    }
  }, [session?.authenticated]);

  // Handle redirects based on auth state
  useEffect(() => {
    // Don't redirect while still initializing
    if (isInitializing) return;

    // Don't redirect on error states (let user see error UI)
    if (isError) return;

    // Redirect authenticated users away from login page
    if (isAuthenticated && isLoginPage && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      navigate('/projects', { replace: true });
      return;
    }

    // Redirect unauthenticated users to login (except if already on login)
    if (!isAuthenticated && !isLoginPage && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      navigate('/login', { replace: true });
      return;
    }
  }, [isAuthenticated, isLoginPage, isInitializing, isError, navigate]);

  // Reset redirect flag when location changes
  useEffect(() => {
    hasRedirectedRef.current = false;
  }, [location.pathname]);

  // Show unified loading screen during initialization
  if (isInitializing) {
    return (
      <div className="flex h-screen items-center justify-center bg-neutral-950">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="md" className="text-white" />
          <div className="text-center">
            <p className="font-medium text-white">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Handle error states (network/server errors only - auth errors redirect)
  if (isError && error) {
    // Auth errors (401, 403) - redirect to login
    if (error.type === 'auth' && !isLoginPage) {
      // Will be handled by the redirect effect above on next render
      return (
        <div className="flex h-screen items-center justify-center bg-neutral-950">
          <Spinner size="md" className="text-white" />
        </div>
      );
    }

    // Network/Server errors - show retry UI (but only on protected routes)
    if (!isLoginPage) {
      const errorTitle = error.type === 'network' ? 'Connection Lost' : 'Server Error';
      const errorDescription = error.type === 'network'
        ? 'Unable to connect to the server. Please check your internet connection.'
        : 'The server is experiencing issues. Please try again.';

      return (
        <div className="flex h-screen items-center justify-center bg-neutral-950 p-4">
          <div className="w-full max-w-md">
            <Alert variant="destructive" className="mb-4 bg-red-950/50 border-red-900">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="ml-2">
                <p className="font-semibold text-red-200">{errorTitle}</p>
                <p className="text-sm mt-1 text-red-300">{errorDescription}</p>
              </AlertDescription>
            </Alert>

            <div className="flex flex-col gap-3">
              <Button
                onClick={() => refetch()}
                className="w-full bg-white text-black hover:bg-neutral-200"
                size="lg"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry Connection
              </Button>
              <Button
                onClick={() => {
                  hasRedirectedRef.current = false;
                  navigate('/login', { replace: true });
                }}
                variant="outline"
                className="w-full border-neutral-700 text-white hover:bg-neutral-800"
                size="lg"
              >
                Go to Login
              </Button>
            </div>

            <p className="text-center text-sm text-neutral-500 mt-4">
              Error: {error.message}
            </p>
          </div>
        </div>
      );
    }
  }

  const contextValue: AuthContextValue = {
    isAuthenticated,
    isInitializing,
    user: session?.user ?? null,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}
